import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BarChartIcon from '@mui/icons-material/BarChart';
import RepeatIcon from '@mui/icons-material/Repeat';
import GroupsIcon from '@mui/icons-material/Groups';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuthStore } from '../../stores/root_store';
import { managerApi } from '../../api/manager_api';
import { TeamActivityPanel } from '../../components/manager/TeamActivityPanel';

// Search history types and helpers
interface SearchHistoryItem {
  email: string;
  userKey: string;
  timestamp: number;
}

const SEARCH_HISTORY_KEY = 'fh_manager_search_history';
const MAX_HISTORY_ITEMS = 20;

function getSearchHistory(): SearchHistoryItem[] {
  try {
    const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function addSearchHistory(email: string, userKey: string): SearchHistoryItem[] {
  const history = getSearchHistory();
  // Remove existing entry for this email if present
  const filtered = history.filter(item => item.email !== email);
  // Add new entry at the beginning
  const updated = [{ email, userKey, timestamp: Date.now() }, ...filtered].slice(0, MAX_HISTORY_ITEMS);
  localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated));
  return updated;
}

export const ManagerPage = observer(() => {
  const navigate = useNavigate();
  const authStore = useAuthStore();

  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);

  // Load search history on mount
  useEffect(() => {
    setSearchHistory(getSearchHistory());
  }, []);

  // Check if current user has manager role
  const currentUserHasManager = authStore.roles.includes('manager');
  const hasSudo = authStore.roles.includes('sudo');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchEmail.trim()) return;

    setSearching(true);
    setError(null);

    try {
      const user = await managerApi.searchByEmail(searchEmail.trim());
      // Save to search history
      const updatedHistory = addSearchHistory(searchEmail.trim(), user.key);
      setSearchHistory(updatedHistory);
      // Navigate to the user edit page
      navigate(`/manager/user/${user.key}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'User not found');
    } finally {
      setSearching(false);
    }
  };

  // Redirect if not a manager
  if (authStore.initialized && !currentUserHasManager) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/assistant')}
        sx={{ mb: 2 }}
      >
        Back to Dashboard
      </Button>

      <Typography variant="h4" gutterBottom>
        Manager Tools
      </Typography>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid size={{ xs: 12, md: 8 }}>
          <TeamActivityPanel />
        </Grid>

        {/* Right Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Button
                variant="contained"
                fullWidth
                startIcon={<BarChartIcon />}
                onClick={() => navigate('/manager/metrics')}
                sx={{ mb: 1 }}
              >
                Metrics Dashboard
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<RepeatIcon />}
                onClick={() => navigate('/manager/recurring-log')}
                sx={{ mb: 1 }}
              >
                Recurring Cron Log
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<VisibilityOffIcon />}
                onClick={() => navigate('/manager/declines')}
                sx={{ mb: 1 }}
              >
                Recent Declines
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<GroupsIcon />}
                onClick={() => navigate('/manager/teams')}
                sx={{ mb: 1 }}
              >
                Teams
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<HowToRegIcon />}
                onClick={() => navigate('/manager/applications')}
              >
                Assistant Applications
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Find User
              </Typography>

              <form onSubmit={handleSearch}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  placeholder="user@example.com"
                  disabled={searching}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={searching || !searchEmail.trim()}
                >
                  {searching ? <CircularProgress size={20} /> : 'Search'}
                </Button>
              </form>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {searchHistory.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Recent Searches
                  </Typography>
                  <List dense disablePadding>
                    {searchHistory.slice(0, 10).map((item) => (
                      <ListItemButton
                        key={item.userKey}
                        onClick={() => navigate(`/manager/user/${item.userKey}`)}
                        sx={{ py: 0.5 }}
                      >
                        <ListItemText primary={item.email} />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>

          {hasSudo && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sudo Tools
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/manager/task-payment')}
                  sx={{ mb: 1 }}
                >
                  Task Payment
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/admin/jobs')}
                >
                  RQ Job Monitor
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  );
});
